import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Form,
  message,
  DatePicker,
  Popconfirm,
  Row,
  Col,
} from "antd";
import { HomeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import UploadFile from "@/components/UplaodFile";
import { CustomUploadNew1 } from "@/hocComponents";

const TYPE_UPLOAD = "Upload Invoice";
const TYPE_UPDATE = "Update Invoice";
const TYPE_VIEW = "View Invoice";

/**
 * Invoice 上传 / 修改 / 查看 弹窗
 * @param {string}   type     
 * @param {object}   record   
 * @param {Function} onClose  
 * @param {Function} onSuccess 
 */
const InvoiceModal = ({ type, record, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [filePaths, setFilePaths] = useState([]);
  const uploadFileRef = useRef(null);
  const customUploadNewRef = useRef(null);

  const open = !!type;
  const isUpload = type === TYPE_UPLOAD;
  const isView = type === TYPE_VIEW;
  const isEditable = isUpload || type === TYPE_UPDATE;

  // 打开 / 关闭时初始化或重置
  useEffect(() => {
    if (!type) {
      form.resetFields();
      setConfirmOpen(false);
      setBtnDisabled(false);
      return;
    }

    if (isUpload) {
      form.setFieldsValue({ invoiceDate: dayjs() });
      return;
    }

    // Update / View：拉取详情。cancelled 用于丢弃过期响应
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await http.get(api.MaterialShowInvoice, {
          params: {
            maintenanceMaterialInvoiceId: record?.maintenanceMaterialInvoiceId,
          },
        });
        if (cancelled) return;
        if (response?.code === "1") {
          const data = response.data || {};
          const fileList = data.fileList || [];
          form.setFieldsValue({
            invoiceAmount: data.invoiceAmount,
            invoiceNumber: data.invoiceNumber,
            invoiceDate: data.invoiceDate ? dayjs(data.invoiceDate) : undefined,
            filePaths: fileList.map((item) => item.relativePath),
          });
          customUploadNewRef.current?.initUpdateList(
            fileList.map((item) => ({
              ...item,
              allPath: item.url,
              name: item?.name || item?.fileName,
            }))
          );
        } else {
          message.error(response?.msg || "Failed to fetch invoice data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  /* ---------------- 文件相关 ---------------- */

  const uploadFiles = async (list) => {
    const files = [];
    for (const item of list) {
      const formData = new FormData();
      formData.append("files", item.originFileObj);
      const response = await http.post(api.UploadFile, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response?.code === "1") files.push(response.data[0]);
    }
    return files;
  };

  // Upload
  const addUpload = async (data) => {
    const uploaded = await uploadFiles(data);
    form.setFieldsValue({ filePaths: uploaded });
  };

  // Update 
  const deleteOldFn = async (file) => {
    const response = await http.get(api.MaterialDeleteInvoiceFile, {
      params: { materialInvoiceFileId: file.id },
    });
    return response?.code === "1";
  };

  // Update 
  const addOldFn = async (file) => {
    setBtnDisabled(true);
    try {
      const uploaded = await uploadFiles(file.filter((item) => !item?.id));
      const prev = form.getFieldValue("filePaths") || [];
      form.setFieldsValue({ filePaths: [...prev, ...uploaded] });

      const response = await http.post(api.MaterialAddInvoiceFile, {
        id: record?.maintenanceMaterialInvoiceId,
        filePaths: uploaded,
      });
      const ok = response?.code === "1";
      return {
        flag: ok,
        fileData: ok ? { ...response.data, type: response.data?.suffix } : null,
      };
    } finally {
      setBtnDisabled(false);
    }
  };

  /* ---------------- 提交 ---------------- */

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const invoiceDateStr = Number(values.invoiceDate);

      const config = isUpload
        ? {
            url: api.MaterialUploadInvoice,
            params: {
              maintenanceId: record?.maintenanceId,
              ...values,
              invoiceDateStr,
            },
            successMsg: "Upload successfully",
          }
        : {
            url: api.MaterialUpdateInvoice,
            params: {
              id: record?.maintenanceMaterialInvoiceId,
              invoiceAmount: values.invoiceAmount,
              invoiceNumber: values.invoiceNumber,
              invoiceDateStr,
            },
            successMsg: "Updated successfully",
          };

      const response = await http.post(config.url, config.params);
      if (response?.code === "1") {
        message.success(config.successMsg);
        onSuccess?.();
      } else {
        message.error(response?.msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 点击 Submit 先校验，通过后才弹出确认框；其它情况（取消 / 点外部）关闭
  const handleConfirmOpenChange = (next) => {
    if (!next) {
      setConfirmOpen(false);
      return;
    }
    form
      .validateFields()
      .then(() => setConfirmOpen(true))
      .catch(() => {});
  };

  /* ---------------- 渲染 ---------------- */

const footer = isEditable ? (
  <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
    <Button onClick={onClose}>Cancel</Button>
    <Popconfirm
      title={<span style={{ fontWeight: 400 }}>Please Confirm invoiceAmount：</span>}
      description={
        <span style={{ fontWeight: 700, fontSize: 26 }}>
          {commonFn.amountToDollarFn(form.getFieldValue("invoiceAmount"))}
        </span>
      }
      open={confirmOpen}
      onOpenChange={handleConfirmOpenChange}
      onConfirm={handleSubmit}
      okText="Confirm"
      cancelText="Cancel"
    >
      <Button type="primary" loading={loading} disabled={btnDisabled}>
        Submit
      </Button>
    </Popconfirm>
  </div>
) : null;

  return (
    <Modal
      title={type}
      width="60%"
      maskClosable={false}
      open={open}
      onCancel={onClose}
      footer={footer}
    >
          <div style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px",marginBottom: "8px" }}></div>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              rules={[{ required: true, message: "Invoice Date is required!" }]}
            >
              <DatePicker
                disabled={isView}
                style={{ width: "100%" }}
                format="MM/DD/YYYY"
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Form.Item>

            {commonFn.formInputNumberDoc({
              name: "invoiceAmount",
              label: "Invoice Amount",
              form,
              disabled: isView,
              validator: (_, value, callback) => {
                if (value >= 1000000) {
                  callback("The Amount Must Be Between 0 And 1000000.");
                } else {
                  callback();
                }
              },
            })}

            <Form.Item
              name="invoiceNumber"
              label="Invoice #"
              rules={[{ required: true, message: "Invoice # is required!" }]}
            >
              <Input disabled={isView} placeholder="Please input Invoice #" />
            </Form.Item>
          </Col>

          <Col span={16}>
            <div style={{ marginBottom: 16, fontWeight: "bold", fontSize: 20 }}>
              <HomeOutlined style={{ marginRight: 8 }} />
              Files
            </div>
            <Form.Item
              name="filePaths"
              label="Files"
              labelCol={{ span: 0 }}
              wrapperCol={{ span: 24 }}
              rules={[{ required: true, message: "Files is required!" }]}
            >
              {isUpload ? (
                <UploadFile ref={uploadFileRef} sendDataToParent={addUpload} />
              ) : (
                <CustomUploadNew1
                  ref={customUploadNewRef}
                  customConfig={{
                    pdfHeight: "76vh",
                    notMultiple: true,
                    accept: ".jpg, .jpeg, .png, .pdf",
                    status: "add",
                    disabled: isView,
                    noUploadBtn: isView,
                    deleteOldFn,
                    addOldFn,
                  }}
                  setFilePaths={setFilePaths}
                  filePaths={filePaths}
                  setBtnDisabled={setBtnDisabled}
                  btnDisabled={btnDisabled}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default InvoiceModal;
